/**
 * Playbook option list view: search + Status/Source filters + sort. Pure so
 * the page and its tests share one definition. Reordering (up/down arrows)
 * only makes sense on the unfiltered list in manual order — see `canReorder`.
 */
import type { StatOption } from '../../lib/api-types';

export type OptionSort = 'manual' | 'name_asc' | 'name_desc' | 'usage' | 'recent';
export type OptionStatusFilter = 'all' | 'enabled' | 'disabled';
export type OptionSourceFilter = 'all' | 'stock' | 'custom';

export interface OptionView {
  search: string;
  sort: OptionSort;
  status: OptionStatusFilter;
  source: OptionSourceFilter;
}

export const DEFAULT_OPTION_VIEW: OptionView = {
  search: '',
  sort: 'manual',
  status: 'all',
  source: 'all',
};

export const SORT_LABELS: Readonly<Record<OptionSort, string>> = {
  manual: 'Manual order',
  name_asc: 'Name A–Z',
  name_desc: 'Name Z–A',
  usage: 'Most used',
  recent: 'Recently added',
};

export const STATUS_LABELS: Readonly<Record<OptionStatusFilter, string>> = {
  all: 'All',
  enabled: 'Enabled',
  disabled: 'Disabled',
};

export const SOURCE_LABELS: Readonly<Record<OptionSourceFilter, string>> = {
  all: 'All',
  stock: 'Stock',
  custom: 'Custom',
};

/** True when search or a filter hides rows (sort alone hides nothing). */
export function isNarrowed(view: OptionView): boolean {
  return view.search.trim() !== '' || view.status !== 'all' || view.source !== 'all';
}

/** Up/down arrows only act on the full list in manual order. */
export function canReorder(view: OptionView): boolean {
  return view.sort === 'manual' && !isNarrowed(view);
}

const byName = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function byManual(a: StatOption, b: StatOption): number {
  return a.sort_order - b.sort_order;
}

export function applyOptionView(items: readonly StatOption[], view: OptionView): StatOption[] {
  const q = view.search.trim().toLowerCase();
  const out = items.filter((o) => {
    if (view.status === 'enabled' && !o.is_enabled) return false;
    if (view.status === 'disabled' && o.is_enabled) return false;
    if (view.source === 'stock' && !o.is_stock) return false;
    if (view.source === 'custom' && o.is_stock) return false;
    if (q === '') return true;
    return o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q);
  });
  switch (view.sort) {
    case 'manual':
      return out;
    case 'name_asc':
      return out.sort((a, b) => byName.compare(a.label, b.label) || byManual(a, b));
    case 'name_desc':
      return out.sort((a, b) => byName.compare(b.label, a.label) || byManual(a, b));
    case 'usage':
      return out.sort(
        (a, b) =>
          b.usage_count - a.usage_count || byName.compare(a.label, b.label) || byManual(a, b)
      );
    case 'recent':
      return out.sort(
        (a, b) =>
          (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0) || byManual(a, b)
      );
  }
}
